import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    makeStyles,
    Radio,
    RadioGroup,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import LinkIcon from '@material-ui/icons/Link'
import { toast } from 'react-toastify'
import { useCompanyDocumentShares } from '../../hooks/useCompanyDocumentShares'
import { CompanyDocument, DocumentShareMode } from '../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        minWidth: 560,
        maxWidth: 640,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 320,
    },
    modeSection: {
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    tenantList: {
        flex: 1,
        minHeight: 200,
        maxHeight: 320,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    tenantRow: {
        padding: theme.spacing(0.75, 1.5),
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    tenantMeta: {
        fontSize: '12px',
        color: theme.palette.text.secondary,
    },
    searchField: {
        marginBottom: theme.spacing(1),
    },
    linkSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
}))

const getLoadErrorMessage = (error: unknown): string => {
    const axiosError = error as {
        response?: { data?: { error?: string; message?: string } }
    }
    return (
        axiosError?.response?.data?.error ||
        axiosError?.response?.data?.message ||
        'Failed to load sharing settings. Please try again.'
    )
}

interface DocumentShareModalProps {
    open: boolean
    onClose: () => void
    document: CompanyDocument | null
    apartmentId: number | null
    apartmentName?: string | null
    activePropertyId: number | null
}

export const DocumentShareModal: React.FC<DocumentShareModalProps> = ({
    open,
    onClose,
    document,
    apartmentId,
    apartmentName,
    activePropertyId,
}) => {
    const classes = useStyles()
    const documentId = document?.id ?? null
    const canManageSharing =
        apartmentId != null &&
        activePropertyId != null &&
        apartmentId === activePropertyId

    const {
        shareState,
        isLoadingShareState,
        shareableTenants,
        isLoadingShareableTenants,
        loadError,
        updateShareState,
        isUpdatingShareState,
    } = useCompanyDocumentShares(
        documentId,
        apartmentId,
        open && canManageSharing,
    )

    const [shareMode, setShareMode] = useState<DocumentShareMode>('none')
    const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([])
    const [searchText, setSearchText] = useState('')

    useEffect(() => {
        if (!open) {
            setSearchText('')
            return
        }
        if (shareState) {
            setShareMode(shareState.share_mode)
            setSelectedTenantIds(shareState.tenant_ids)
        }
    }, [open, shareState])

    const docLinkedToProperty =
        document != null &&
        apartmentId != null &&
        (document.company_ids ?? document.companies ?? []).includes(apartmentId)

    const filteredTenants = useMemo(() => {
        if (!searchText.trim()) return shareableTenants
        const q = searchText.trim().toLowerCase()
        return shareableTenants.filter(
            (t) =>
                t.name?.toLowerCase().includes(q) ||
                t.email?.toLowerCase().includes(q) ||
                (t.unit_label?.toLowerCase().includes(q) ?? false),
        )
    }, [shareableTenants, searchText])

    const handleToggleTenant = (tenantId: number) => {
        setShareMode('none')
        setSelectedTenantIds((prev) =>
            prev.includes(tenantId)
                ? prev.filter((id) => id !== tenantId)
                : [...prev, tenantId],
        )
    }

    const handleSave = async () => {
        if (!documentId || !apartmentId) return
        try {
            await updateShareState.mutateAsync({
                apartment_id: apartmentId,
                share_mode: shareMode,
                tenant_ids:
                    shareMode === 'none' ? selectedTenantIds : undefined,
            })
            toast.success('Sharing settings updated')
            onClose()
        } catch (error: any) {
            const message =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update sharing'
            toast.error(message)
        }
    }

    const handleCopyLink = async () => {
        const url = document?.document
        if (!url) {
            toast.error('No document link available')
            return
        }
        try {
            await navigator.clipboard.writeText(url)
            toast.success('Document link copied to clipboard.')
        } catch {
            const textArea = window.document.createElement('textarea')
            textArea.value = url
            window.document.body.appendChild(textArea)
            textArea.select()
            window.document.execCommand('copy')
            window.document.body.removeChild(textArea)
            toast.success('Document link copied to clipboard.')
        }
    }

    const isLoading = isLoadingShareState || isLoadingShareableTenants
    const isLoadError = loadError != null
    const individualModeDisabled =
        shareMode === 'all_current' || shareMode === 'all_current_and_future'
    const workspaceMismatchMessage = apartmentName
        ? `Switch to ${apartmentName}'s workspace to manage tenant sharing.`
        : "Switch to this property's workspace to manage tenant sharing."

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Share with tenants
                {document && (
                    <Typography variant="body2" color="textSecondary">
                        {document.name}
                        {apartmentName ? ` · ${apartmentName}` : ''}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent className={classes.content}>
                {document && (
                    <Box className={classes.linkSection}>
                        <Box>
                            <Typography variant="subtitle2">
                                Document link
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Copy the public URL to share outside the tenant
                                portal.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={handleCopyLink}
                            disabled={!document.document}
                        >
                            Copy link
                        </Button>
                    </Box>
                )}

                {!canManageSharing ? (
                    <Typography color="textSecondary">
                        {workspaceMismatchMessage}
                    </Typography>
                ) : !docLinkedToProperty ? (
                    <Typography color="textSecondary">
                        This document is not linked to the selected property.
                        Add the property to the document before sharing.
                    </Typography>
                ) : isLoading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={200}
                    >
                        <CircularProgress />
                    </Box>
                ) : isLoadError ? (
                    <Typography color="error">
                        {getLoadErrorMessage(loadError)}
                    </Typography>
                ) : (
                    <>
                        <Box className={classes.modeSection}>
                            <Typography variant="subtitle2" gutterBottom>
                                Sharing mode
                            </Typography>
                            <RadioGroup
                                value={shareMode}
                                onChange={(e) =>
                                    setShareMode(
                                        e.target.value as DocumentShareMode,
                                    )
                                }
                            >
                                <FormControlLabel
                                    value="none"
                                    control={
                                        <Radio color="primary" size="small" />
                                    }
                                    label="Select individual tenants"
                                />
                                <FormControlLabel
                                    value="all_current"
                                    control={
                                        <Radio color="primary" size="small" />
                                    }
                                    label="Share with all current tenants"
                                />
                                <FormControlLabel
                                    value="all_current_and_future"
                                    control={
                                        <Radio color="primary" size="small" />
                                    }
                                    label="Share with all current and future tenants"
                                />
                            </RadioGroup>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Tenants
                            </Typography>
                            <TextField
                                className={classes.searchField}
                                placeholder="Search tenants..."
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={individualModeDisabled}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                color="action"
                                                fontSize="small"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box className={classes.tenantList}>
                                {filteredTenants.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ padding: 16 }}
                                    >
                                        No tenants found for this property.
                                    </Typography>
                                ) : (
                                    filteredTenants.map((tenant) => (
                                        <Box
                                            key={tenant.id}
                                            className={classes.tenantRow}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        color="primary"
                                                        checked={
                                                            shareMode !== 'none'
                                                                ? shareMode ===
                                                                      'all_current' ||
                                                                  shareMode ===
                                                                      'all_current_and_future'
                                                                : selectedTenantIds.includes(
                                                                      tenant.id,
                                                                  )
                                                        }
                                                        disabled={
                                                            individualModeDisabled
                                                        }
                                                        onChange={() =>
                                                            handleToggleTenant(
                                                                tenant.id,
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {tenant.name ||
                                                                tenant.email}
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.tenantMeta
                                                            }
                                                        >
                                                            {tenant.email}
                                                            {tenant.unit_label
                                                                ? ` · ${tenant.unit_label}`
                                                                : ''}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isUpdatingShareState}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={
                        !canManageSharing ||
                        !docLinkedToProperty ||
                        isUpdatingShareState ||
                        isLoading ||
                        isLoadError
                    }
                >
                    {isUpdatingShareState ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
